'use client';

import {
  TextField as MuiTextField,
  type TextFieldProps as MuiTextFieldProps,
  Stack,
  type SxProps,
  styled,
} from '@mui/material';
import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';

import { pluralize } from '@campaign-manager/core/lib/utils';
import { campaignManagerTheme } from '@campaign-manager/theme';

import Label from '../Label';
import Text from '../Text';

const RmtTextField = styled(MuiTextField, {
  name: 'RmtTextField',
})(() => ({
  '& .MuiFilledInput-root.Mui-error': {
    backgroundColor: campaignManagerTheme.color.paleRedOverlay,
    border: `2px solid ${campaignManagerTheme.color.red}`,
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    backgroundColor: campaignManagerTheme.color.paleRedOverlay,
    border: `2px solid ${campaignManagerTheme.color.red}`,
  },
}));

export type Props = MuiTextFieldProps & {
  label?: ReactNode;
  descriptionText?: string;
  inputRef?: Ref<HTMLInputElement>;
  tooltipText?: ReactNode | string;
  // v9 removed TextFieldProps['inputProps']. This stays as a convenience prop —
  // it is spread into slotProps.htmlInput below — but it cannot borrow MUI's slot
  // type: SlotProps is a union with a callback form, so reading `.sx` off it (as
  // the merge does) does not typecheck. Declaring what we actually accept.
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { sx?: SxProps };
  min?: number;
  max?: number;
  containerSx?: SxProps;
  autocomplete?: 'on' | 'off';
  showCharCounter?: boolean;
  lengthLimit?: number;
};

export default function TextField({
  label,
  descriptionText,
  inputRef,
  tooltipText,
  inputProps,
  min,
  max,
  containerSx,
  autocomplete,
  showCharCounter,
  lengthLimit,
  slotProps,
  ...props
}: Props) {
  const id = useId();

  const ref = useRef<HTMLInputElement>(null);
  const [count, setCount] = useState((props.value ?? '').toString().length);
  const updateCount = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCount((e.target.value ?? '').length);
  }, []);

  return (
    <Stack
      sx={[
        {
          gap: 0.5,
          alignItems: 'flex-start',
        },
        ...(Array.isArray(containerSx) ? containerSx : [containerSx]),
      ]}
    >
      {label && (
        <Label
          id={id}
          tooltipText={tooltipText}
        >
          {label}
        </Label>
      )}
      {descriptionText && (
        <Text
          size="small"
          color="grey"
        >
          {descriptionText}
        </Text>
      )}
      <RmtTextField
        {...props}
        hiddenLabel
        id={id}
        ref={inputRef}
        slotProps={{
          ...slotProps,
          htmlInput: {
            ...inputProps,
            ...slotProps?.htmlInput,
            ...{
              ref,
              min,
              max,
              sx: {
                ...inputProps?.sx,
                ...{ fontSize: campaignManagerTheme.fontSize.sm },
              },
              onChange: showCharCounter ? updateCount : undefined,
            },
          },
          formHelperText: {
            sx: {
              marginLeft: 0,
              fontWeight: 500,
              fontSize: '0.875rem',
            },
          },
        }}
      />
      {showCharCounter && (
        <Text
          color="grey"
          size="xs"
          weight="medium"
        >
          {count}
          {lengthLimit ? `/${lengthLimit}` : ''} {pluralize(count, 'character')}
        </Text>
      )}
    </Stack>
  );
}
